import { useCallback, useMemo, useRef, useState } from "react";
import { useAgentsTargets } from "@/modules/agents/hooks/agents-targets.hook";
import { getQuarterInfo, getQuarterStartDate } from "@/modules/targets/utils";
import { getQuarter } from "date-fns";
import { AgentTargetDTO, UserType } from "@/modules/agents/dtos/index.v2";
import { targetsData } from "@/modules/targetsv2/types";

// Transform API data to TargetCard format - moved to shared function
export const transformToTargetCardData = (
  agentTarget: AgentTargetDTO
): targetsData[] => {
  // Handle case where there are no targets
  if (!agentTarget.targets || agentTarget.targets.length === 0) {
    return [
      {
        period: "M1",
        current: "0",
        target: "0",
        percentage: 0,
        userType: undefined, // No targets means no userType
      },
      {
        period: "M2",
        current: "0",
        target: "0",
        percentage: 0,
        userType: undefined,
      },
      {
        period: "M3",
        current: "0",
        target: "0",
        percentage: 0,
        userType: undefined,
      },
      {
        period: "Total",
        current: "0",
        target: "0",
        percentage: 0,
        userType: undefined,
      },
    ];
  }

  // Sort targets by from date
  const sortedTargets = [...agentTarget.targets].sort(
    (a, b) => new Date(a.from).getTime() - new Date(b.from).getTime()
  );

  // Helper to format numbers as readable strings
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US").format(amount || 0);
  };

  // Helper to capitalize effectiveTargetType for display
  const formatEffectiveTargetType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Create monthly data (M1, M2, M3) - pad with zeros if less than 3 months
  const monthlyData: targetsData[] = [];
  for (let i = 0; i < 3; i++) {
    const target = sortedTargets[i];
    if (target) {
      // Type assertion since API response includes effectiveTarget but DTO doesn't
      const targetWithEffective = target as any;
      const effectiveTargetVal = targetWithEffective.effectiveTarget || 0; // Use effectiveTarget from API
      const teamAchVal = target.teamAchievement || 0;
      const personalTargetVal = target.personalTarget || 0;
      const personalAchVal = target.personalAchievement || 0;
      const extraAchVal = target.extraAchievement || 0; // Extract extra achievement
      const userType = target.userType as UserType; // Cast userType string to UserType enum

      // TEAM ROW (only if effective target actually exists)
      if (effectiveTargetVal > 0) {
        const percentage =
          effectiveTargetVal > 0 ? (teamAchVal / effectiveTargetVal) * 100 : 0;

        monthlyData.push({
          period: `M${i + 1}`,
          current: formatCurrency(teamAchVal),
          target: formatCurrency(effectiveTargetVal),
          percentage: Math.round(percentage),
          effectiveTargetType: target.effectiveTargetType
            ? formatEffectiveTargetType(target.effectiveTargetType)
            : undefined,
          targetType: "team" as const,
          extraAchievement: extraAchVal,
          userType: userType,
        });
      }

      // PERSONAL ROW (if personal target exists OR personal achievement exists, and effective target exists as well)
      if (
        (personalTargetVal > 0 || personalAchVal > 0) &&
        effectiveTargetVal > 0
      ) {
        const percentage =
          personalTargetVal > 0
            ? (personalAchVal / personalTargetVal) * 100
            : personalAchVal > 0
            ? Infinity // Special value to indicate over-achievement with zero target
            : 0; // 0% if no target but has achievement

        monthlyData.push({
          period: `M${i + 1}`,
          current: formatCurrency(personalAchVal),
          target: formatCurrency(personalTargetVal),
          percentage: Math.round(percentage),
          effectiveTargetType: target.effectiveTargetType
            ? formatEffectiveTargetType(target.effectiveTargetType)
            : undefined,
          targetType: "personal" as const,
          extraAchievement: extraAchVal,
          userType: userType,
        });
      }

      // PERSONAL ONLY (when no effective target but personal target OR personal achievement exists)
      if (
        (personalTargetVal > 0 || personalAchVal > 0) &&
        effectiveTargetVal === 0
      ) {
        const percentage =
          personalTargetVal > 0
            ? (personalAchVal / personalTargetVal) * 100
            : personalAchVal > 0
            ? Infinity // Special value to indicate over-achievement with zero target
            : 0; // 0% if no target but has achievement

        monthlyData.push({
          period: `M${i + 1}`,
          current: formatCurrency(personalAchVal),
          target: formatCurrency(personalTargetVal),
          percentage: Math.round(percentage),
          effectiveTargetType: target.effectiveTargetType
            ? formatEffectiveTargetType(target.effectiveTargetType)
            : undefined,
          targetType: "personal" as const,
          extraAchievement: extraAchVal,
          userType: userType,
        });
      }

      // FALLBACK: If both targets are 0, create a team row with zeros
      if (effectiveTargetVal === 0 && personalTargetVal === 0) {
        monthlyData.push({
          period: `M${i + 1}`,
          current: "0",
          target: "0",
          percentage: 0,
          effectiveTargetType: target.effectiveTargetType
            ? formatEffectiveTargetType(target.effectiveTargetType)
            : undefined,
          targetType: "team" as const,
          extraAchievement: extraAchVal,
          userType: userType,
        });
      }
    } else {
      // Fill missing months with zeros - no userType available
      monthlyData.push({
        period: `M${i + 1}`,
        current: "0",
        target: "0",
        percentage: 0,
        effectiveTargetType: undefined,
        targetType: "team" as const,
        extraAchievement: 0,
        userType: undefined,
      });
    }
  }

  // Calculate totals for effective and personal separately
  const totalEffectiveTarget = sortedTargets.reduce(
    (sum, t) => sum + ((t as any).effectiveTarget || 0),
    0
  );
  const totalTeamAch = sortedTargets.reduce(
    (sum, t) => sum + (t.teamAchievement || 0),
    0
  );

  const totalPersonalTarget = sortedTargets.reduce(
    (sum, t) => sum + (t.personalTarget || 0),
    0
  );
  const totalPersonalAch = sortedTargets.reduce(
    (sum, t) => sum + (t.personalAchievement || 0),
    0
  );

  // Calculate total extra achievements
  const totalExtraAch = sortedTargets.reduce(
    (sum, t) => sum + ((t as any).extraAchievement || 0),
    0
  );

  // Get the most common effectiveTargetType for the total column (only from defined values)
  const effectiveTargetTypes = sortedTargets
    .map((target) => target.effectiveTargetType)
    .filter((type): type is string => type !== undefined && type !== null) // Type guard
    .map((type) => formatEffectiveTargetType(type));

  const mostCommonType =
    effectiveTargetTypes.length > 0
      ? effectiveTargetTypes.reduce((a, b, _, arr) =>
          arr.filter((v) => v === a).length >= arr.filter((v) => v === b).length
            ? a
            : b
        )
      : undefined;

  // TEAM TOTAL (only if effective target actually exists)
  if (totalEffectiveTarget > 0) {
    monthlyData.push({
      period: "Total",
      current: formatCurrency(totalTeamAch),
      target: formatCurrency(totalEffectiveTarget),
      percentage:
        totalEffectiveTarget > 0
          ? Math.round((totalTeamAch / totalEffectiveTarget) * 100)
          : 0,
      effectiveTargetType: mostCommonType,
      targetType: "team" as const,
      extraAchievement: totalExtraAch,
      userType: undefined,
    });
  }

  // PERSONAL TOTAL (if personal target exists OR personal achievement exists)
  if (totalPersonalTarget > 0 || totalPersonalAch > 0) {
    monthlyData.push({
      period: "Total",
      current: formatCurrency(totalPersonalAch),
      target: formatCurrency(totalPersonalTarget),
      percentage:
        totalPersonalTarget > 0
          ? Math.round((totalPersonalAch / totalPersonalTarget) * 100)
          : 0, // 0% if no target but has achievement
      effectiveTargetType: mostCommonType,
      targetType: "personal" as const,
      extraAchievement: totalExtraAch,
      userType: undefined,
    });
  }

  // FALLBACK TOTAL: If no targets exist at all, create a team total with zeros
  if (totalEffectiveTarget === 0 && totalPersonalTarget === 0) {
    monthlyData.push({
      period: "Total",
      current: "0",
      target: "0",
      percentage: 0,
      effectiveTargetType: mostCommonType,
      targetType: "team" as const,
      extraAchievement: totalExtraAch,
      userType: undefined,
    });
  }

  return monthlyData;
};

interface UseTargetsPageReturn {
  // State
  selectedYear: number;
  selectedQuarter: 1 | 2 | 3 | 4;
  selectedUserUId: string | null;
  threadHeights: Record<string, number>;
  expandedNodes: Record<string, boolean>;

  // Handlers
  handleYearChange: (year: number) => void;
  handleQuarterChange: (quarter: 1 | 2 | 3 | 4) => void;
  handleUserChange: (userUId: string | null) => void;
  handleThreadHeightChange: (height: number, cognitoId: string) => void;

  // Data
  quarterInfo: {
    startDate: Date;
    endDate: Date;
  };
  rootUsers: any[];
  getExtraTopMargin: (index: number) => number;
  transformToTargetCardData: (agentTarget: AgentTargetDTO) => targetsData[];
  isLoading: boolean;
}

export const useTargetsPage = (): UseTargetsPageReturn => {
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );

  const handleYearChange = useCallback((year: number) => {
    setSelectedYear(year);
    setSelectedQuarter(1);
  }, []);

  const [selectedQuarter, setSelectedQuarter] = useState<1 | 2 | 3 | 4>(
    getQuarter(new Date()) as 1 | 2 | 3 | 4
  );

  const handleQuarterChange = useCallback((quarter: 1 | 2 | 3 | 4) => {
    setSelectedQuarter(quarter);
  }, []);

  const quarterInfo = useMemo(
    () => getQuarterInfo(getQuarterStartDate(selectedYear, selectedQuarter)),
    [selectedYear, selectedQuarter]
  );

  const [selectedUserUId, setSelectedUserUId] = useState<string | null>(null);

  const handleUserChange = useCallback((userUId: string | null) => {
    setSelectedUserUId(userUId);
  }, []);

  const { data: agentsTargetsResponse, isLoading } = useAgentsTargets({
    query: {
      from: quarterInfo.startDate.toISOString(),
      to: quarterInfo.endDate.toISOString(),
      ...(selectedUserUId && { userUId: selectedUserUId }),
    },
  });

  const rootUsers = agentsTargetsResponse?.data || [];

  // Track thread heights of each root node
  const [threadHeights, setThreadHeights] = useState<Record<string, number>>(
    {}
  );

  // Track which nodes are expanded/opened
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>(
    {}
  );

  // Use ref to store the latest threadHeights to avoid stale closures
  const threadHeightsRef = useRef(threadHeights);
  threadHeightsRef.current = threadHeights;

  // Use ref to store the latest expansion states
  const expandedNodesRef = useRef(expandedNodes);
  expandedNodesRef.current = expandedNodes;

  // Simple single callback function
  const handleThreadHeightChange = useCallback(
    (height: number, cognitoId: string) => {
      setThreadHeights((prev) => {
        if (prev[cognitoId] === height) return prev;
        return {
          ...prev,
          [cognitoId]: height,
        };
      });

      // Track expansion state based on thread height
      const BASE_HEIGHT = 80;
      const isExpanded = height > BASE_HEIGHT;

      setExpandedNodes((prev) => {
        if (prev[cognitoId] === isExpanded) return prev;
        return {
          ...prev,
          [cognitoId]: isExpanded,
        };
      });
    },
    []
  );

  // Calculate extra top margin for each node based on previous node's thread height
  const getExtraTopMargin = useCallback(
    (index: number) => {
      if (index === 0) return 0; // First node has no extra margin

      const previousUser = rootUsers[index - 1];
      const previousThreadHeight =
        threadHeightsRef.current[previousUser?.user?.cognitoId] || 0;
      const isPreviousExpanded =
        expandedNodesRef.current[previousUser?.user?.cognitoId] || false;

      // Only add spacing if the previous node is actually expanded
      if (isPreviousExpanded && previousThreadHeight > 0) {
        // Now we get the actual visual height, so just add some padding
        return previousThreadHeight + 25;
      }

      return 0; // No extra spacing if previous node is collapsed
    },
    [rootUsers]
  );

  return {
    // State
    selectedYear,
    selectedQuarter,
    selectedUserUId,
    threadHeights,
    expandedNodes,

    // Handlers
    handleYearChange,
    handleQuarterChange,
    handleUserChange,
    handleThreadHeightChange,

    // Data
    quarterInfo,
    rootUsers,
    getExtraTopMargin,
    transformToTargetCardData,
    isLoading,
  };
};
